export function normalizeVietnamese(str) {
  return str.trim().toLowerCase().replace(/[.?!,]/g, '').replace(/\s+/g, ' ').trim()
}

// Diacritic-insensitive comparison key: strips Vietnamese tone marks AND letter
// modifications (ă â ê ô ơ ư → a a e o o u, đ → d), then applies the same
// lowercase/punctuation/whitespace normalization as normalizeVietnamese.
export function stripDiacritics(str) {
  const base = str
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // drop combining marks (tones + ă/â/ê/ô hats, ơ/ư horns)
    .replace(/[đĐ]/g, 'd')
    // ơ and ư carry a horn (U+031B) which NFD already separates above; the bare
    // letters are then plain o/u. No extra mapping needed.
  return normalizeVietnamese(base)
}

// Southern Vietnamese collapses the hỏi (hook above, U+0309) and ngã (tilde,
// U+0303) tones into a single dipping tone — mả and mã are pronounced (and, for
// our purposes, spelled-equivalently) the same. Decompose to NFD and map the
// combining tilde onto the combining hook so the two compare equal, leaving every
// other tone mark (sắc/huyền/nặng) and letter modifier (ă/â/ê/ô/ơ/ư/đ) untouched.
export function mergeHoiNga(str) {
  return str.normalize('NFD').replace(/\u0303/g, '\u0309').normalize('NFC')
}

// Comparison key that applies the Southern hỏi/ngã merger on top of the standard
// lowercase/punctuation/whitespace normalization.
export function normalizeSouthernTones(str) {
  return normalizeVietnamese(mergeHoiNga(str))
}

// Vietnamese permits two conventions for WHERE the tone mark sits on the open
// oa/oe/uy rimes: modern `khỏe`/`hòa`/`thúy` marks the first vowel, traditional
// `khoẻ`/`hoà`/`thuý` the second. They are the same word, and IMEs differ on
// which they emit — so a learner using the traditional convention was being
// marked wrong. These are distinct character sequences that neither NFC nor NFD
// unifies, so canonicalise by lifting each syllable's tone mark to the end of
// the token. Placement stops mattering; WHICH tone it is still does, so `khoé`
// (sắc) is still not `khỏe` (hỏi). Letter modifiers (ă â ê ô ơ ư) are not tone
// marks and stay in place.
const TONE_MARKS = /[̣̀́̃̉]/g

export function liftToneMarks(str) {
  return str
    .normalize('NFD')
    .split(/(\s+)/)
    .map((token) => {
      const marks = token.match(TONE_MARKS)
      return marks ? token.replace(TONE_MARKS, '') + marks.join('') : token
    })
    .join('')
}

// Comparison key that ignores tone-mark PLACEMENT while still respecting the
// Southern hỏi/ngã merger.
export function normalizeTonePlacement(str) {
  return normalizeVietnamese(liftToneMarks(mergeHoiNga(str)))
}

// Compares a typed answer against the correct answer (and any acceptable
// alternatives). In strict mode an exact match (after normalizeVietnamese) is
// required. In lenient mode, an answer that matches once diacritics are stripped
// is accepted but flagged with toneMismatch so the UI can nudge the learner.
// A hỏi/ngã (hook/tilde) difference is always accepted as fully correct — even in
// strict mode — because the two are the same tone in Southern Vietnamese.
export function checkVietnameseAnswer(given, correct, acceptables = [], { strict = true } = {}) {
  const candidates = [correct, ...acceptables].filter(c => c != null)
  const normGiven = normalizeVietnamese(given)
  if (candidates.some(c => normalizeVietnamese(c) === normGiven)) {
    return { isCorrect: true, toneMismatch: false }
  }
  const southernGiven = normalizeSouthernTones(given)
  if (candidates.some(c => normalizeSouthernTones(c) === southernGiven)) {
    return { isCorrect: true, toneMismatch: false }
  }
  // Same word, other tone-mark convention (khoẻ vs khỏe) — fully correct, and
  // not a tone mistake, so it must not raise toneMismatch either.
  const placementGiven = normalizeTonePlacement(given)
  if (candidates.some(c => normalizeTonePlacement(c) === placementGiven)) {
    return { isCorrect: true, toneMismatch: false }
  }
  if (!strict) {
    const strippedGiven = stripDiacritics(given)
    if (candidates.some(c => stripDiacritics(c) === strippedGiven)) {
      return { isCorrect: true, toneMismatch: true }
    }
  }
  return { isCorrect: false, toneMismatch: false }
}

export function levenshteinSimilarity(a, b) {
  if (a === b) return 1
  if (!a.length || !b.length) return 0
  const matrix = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  )
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      matrix[i][j] = a[i - 1] === b[j - 1]
        ? matrix[i - 1][j - 1]
        : 1 + Math.min(matrix[i - 1][j], matrix[i][j - 1], matrix[i - 1][j - 1])
    }
  }
  return 1 - matrix[a.length][b.length] / Math.max(a.length, b.length)
}
